import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tooltip,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Added import
import { fetchAvailability } from '../../services/public.services';
import { FormDatas } from '../../types/FormData';
import dayjs from 'dayjs';
import 'dayjs/locale/de'; // Import German locale
dayjs.locale('de'); // Set Day.js to use German locale globally
import { styled, Theme } from '@mui/material/styles'; // Correct import
import { grey } from '@mui/material/colors'; // Added import
import { useTranslation } from 'react-i18next';

// Utility function to get the start of the week (Monday)
const getStartOfWeek = (date: Date): Date => {
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Adjust when day is Sunday
  const start = new Date(date);
  start.setDate(date.getDate() + diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

// Utility function to add weeks
const addWeeks = (date: Date, weeks: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + weeks * 7);
  return newDate;
};

// Generate an array of dates for the current week
const generateWeekDates = (startOfWeek: Date): Date[] => {
  const week: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    week.push(d);
  }
  return week;
};

// Styled Components for Enhanced Design
const StyledCard = styled(Card)(({ theme }: { theme: Theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  borderRadius: '16px',
  textAlign: 'center',
  padding: '1.5em',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
  },
  border: `2px solid ${theme.palette.primary.main}`,
}));

const DisabledCard = styled(Card)(({ theme }: { theme: Theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  borderRadius: '16px',
  textAlign: 'center',
  padding: '1.5em',
  backgroundColor: grey[200],
  color: grey[600],
  cursor: 'not-allowed',
  boxShadow: theme.shadows[1],
  border: `2px solid ${grey[400]}`,
}));

const SlotChip = styled(Chip)(({ theme }: { theme: Theme }) => ({
  cursor: 'pointer',
  transition: 'transform 0.2s, background-color 0.3s',
  '&:hover': {
    transform: 'scale(1.05)',
    backgroundColor: theme.palette.success.dark,
  },
}));

interface ScheduleAppointmentProps {
  formData: FormDatas;
  setFormData: React.Dispatch<React.SetStateAction<FormDatas>>;
}

const ScheduleAppointment: React.FC<ScheduleAppointmentProps> = ({ formData, setFormData }) => {
  const [appointments, setAppointments] = useState<string[]>([]); // Booked slots
  const [availableSlots, setAvailableSlots] = useState<{ [date: string]: string[] }>({}); // Available slots by date
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(getStartOfWeek(new Date())); // Start of the selected week
  const [selectedDate, setSelectedDate] = useState<Date | null>(null); // Selected date
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null); // Selected slot (e.g., "13:00")
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  const { t } = useTranslation(); 

  const locationId = formData.locationId || 1; // Replace with the selected location ID

  useEffect(() => {
    const fetchSlots = async () => {
      setLoading(true);
      try {
        const weekStartDate = dayjs(selectedWeekStart).format('YYYY-MM-DD'); // Start of the selected week
        const response = await fetchAvailability(locationId, weekStartDate); // Fetch availability

        const slotsByDate: { [date: string]: string[] } = {};
        const bookedSlots: string[] = [];

        // Parse the response
        response.availability.forEach((day: { date: string; slots: { time: string; remainingCapacity: number }[] }) => {
          // Only include slots with remainingCapacity > 0
          const available = day.slots.filter((slot) => slot.remainingCapacity > 0).map((slot) => slot.time);
          if (available.length > 0) {
            slotsByDate[day.date] = available;
          }
          day.slots.forEach((slot) => {
            if (slot.remainingCapacity === 0) {
              bookedSlots.push(`${day.date} ${slot.time}`);
            }
          });
        });

        setAvailableSlots(slotsByDate);
        setAppointments(bookedSlots);
        setSelectedDate(null); // Reset selected date when week changes
        setSelectedSlot(null); // Reset selected slot when week changes
        setError(null);
      } catch (err) {
        console.error('Error fetching availability:', err);
        setError('Failed to fetch availability. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [locationId, selectedWeekStart]);

  const handleWeekChange = (weeks: number) => {
    const newWeekStart = addWeeks(selectedWeekStart, weeks);
    setSelectedWeekStart(newWeekStart);
  };

  const handleDateSelect = (date: Date, isAvailable: boolean) => {
    if (!isAvailable) return;
    setSelectedDate(date);
    setSelectedSlot(null); // Reset slot when changing date
  };

  const handleSlotSelect = (slot: string) => {
    if (!selectedDate) return;

    setSelectedSlot(slot);
    setConfirmationOpen(true);
  };

  const confirmSlotSelection = () => {
    if (!selectedDate || !selectedSlot) return;

    const [hours, minutes] = selectedSlot.split(':').map(Number);
    const appointmentDate = new Date(selectedDate);
    appointmentDate.setHours(hours, minutes, 0, 0);

    setFormData({ ...formData, appointment: appointmentDate });
    setConfirmationOpen(false);
  };

  const cancelSlotSelection = () => {
    setConfirmationOpen(false);
    setSelectedSlot(null);
  };

  const renderSlots = () => {
    if (!selectedDate)
      return <Typography>Wählen Sie ein Datum, um verfügbare Zeitfenster zu sehen.</Typography>;

    const dateString = dayjs(selectedDate).format('YYYY-MM-DD');
    const slots = availableSlots[dateString];

    if (!slots || slots.length === 0) {
      return <Typography>Keine verfügbaren Zeitfenster für dieses Datum.</Typography>;
    }

    return (
      <Grid container spacing={2} justifyContent="center">
        {slots.map((hour) => {
          const isBooked = appointments.includes(`${dateString} ${hour}`);
          const isSelected = selectedSlot === hour;

          return (
            <Grid item key={hour}>
              <Tooltip title={isBooked ? 'Slot ist gebucht' : 'Klicken, um diesen Slot auszuwählen'} arrow>
                <SlotChip
                  label={hour}
                  color={isBooked ? 'default' : isSelected ? 'primary' : 'success'}
                  clickable={!isBooked}
                  onClick={() => !isBooked && handleSlotSelect(hour)}
                  sx={{
                    cursor: isBooked ? 'not-allowed' : 'pointer',
                    opacity: isBooked ? 0.6 : 1,
                    transition: 'transform 0.2s, background-color 0.3s',
                    '&:hover': {
                      transform: isBooked ? 'none' : 'scale(1.05)',
                      backgroundColor: isBooked
                        ? 'default'
                        : isSelected
                        ? 'primary.dark'
                        : 'success.dark',
                    },
                  }}
                />
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const weekDates = generateWeekDates(selectedWeekStart);

  // Determine today's date without time
  const today = dayjs().startOf('day');

  return (
    <Box
      sx={{
        padding: { xs: '1em', sm: '2em' },
        maxWidth: '900px',
        margin: 'auto',
        borderRadius: '16px',
        boxShadow: 3,
        backgroundColor: '#ffffff',
      }}
    >
      <Typography variant="h2" sx={{ mb: 2, textAlign: 'center', fontWeight: '700', color: '#333' }}>
      Vereinbare deinen Termin
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: '#666' }}>
      Wählen Sie ein Datum und einen verfügbaren Zeitraum aus, um Ihren Termin zu buchen.
      </Typography>

      {error ? (
        <Typography color="error" sx={{ textAlign: 'center' }}>
          {error}
        </Typography>
      ) : (
        <>
          {/* Week Navigation */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleWeekChange(-1)}
              startIcon={<ArrowBack />}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
              disabled={dayjs(selectedWeekStart).isBefore(today, 'week')}
            >
              Zurück
            </Button>
            <Typography variant="h6" sx={{ minWidth: '200px', textAlign: 'center', color: '#333' }}>
              Woche von {dayjs(selectedWeekStart).format('D. MMMM YYYY')}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleWeekChange(1)}
              endIcon={<ArrowForward />}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              Weiter
            </Button>
          </Box>

          {/* Week Dates */}
          <Grid container spacing={2} justifyContent="center">
            {weekDates.map((date) => {
              const dateString = dayjs(date).format('YYYY-MM-DD');
              const isAvailable = availableSlots.hasOwnProperty(dateString) && availableSlots[dateString].length > 0;
              const isSelected = selectedDate && dayjs(selectedDate).isSame(date, 'day');
              const isPast = dayjs(date).isBefore(today, 'day');

              if (isPast) {
                // Render past dates as disabled
                return (
                  <Grid item xs={12} sm={6} md={3} key={dateString}>
                    <Card sx={{
                      backgroundColor: '#f0f0f0', color: '#aaa', textAlign: 'center', padding: '1em',
                      cursor: 'default', opacity: 0.5
                    }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
                          {dayjs(date).format('ddd')}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: '700' }}>
                          {dayjs(date).format('D')}
                        </Typography>
                        <Typography variant="body2">Nicht verfügbar</Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                );
              }

              return isAvailable ? (
                <Grid item xs={12} sm={6} md={3} key={dateString}>
                  <StyledCard
                    variant="outlined"
                    sx={{
                      backgroundColor: isSelected ? '#1976d2' : '#fff',
                      color: isSelected ? '#fff' : '#1976d2',
                    }}
                    onClick={() => handleDateSelect(date, isAvailable)}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
                        {dayjs(date).format('ddd')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: '700' }}>
                        {dayjs(date).format('D')}
                      </Typography>
                      <Typography variant="body2">{isAvailable ? 'Verfügbar' : 'Nicht verfügbar'}</Typography>
                      {isAvailable && <CheckCircleIcon color="success" />} {/* Example Icon */}
                    </CardContent>
                  </StyledCard>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} md={3} key={dateString}>
                  <DisabledCard>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
                        {dayjs(date).format('ddd')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: '700' }}>
                        {dayjs(date).format('D')}
                      </Typography>
                      <Typography variant="body2">Nicht verfügbar</Typography>
                    </CardContent>
                  </DisabledCard>
                </Grid>
              );
            })}
          </Grid>

          {/* Available Slots */}
          <Box
            sx={{
              marginTop: '3em',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: '600' }}>
              {selectedDate
                ? `Verfügbare Zeitfenster für ${dayjs(selectedDate).format('dddd, D. MMMM YYYY')}`
                : 'Wählen Sie ein Datum, um verfügbare Zeitfenster anzuzeigen.'}
            </Typography>
            {loading ? (
              <CircularProgress />
            ) : (
              <Card
                variant="outlined"
                sx={{
                  width: '100%',
                  maxWidth: '600px',
                  padding: '2em',
                  borderRadius: '16px',
                  boxShadow: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                {renderSlots()}
              </Card>
            )}
          </Box>

          {/* Selected Appointment */}
          {selectedDate && selectedSlot && (
            <Box
              sx={{
                marginTop: '2em',
                textAlign: 'center',
                padding: '1.5em',
                backgroundColor: '#e3f2fd',
                borderRadius: '16px',
                boxShadow: 1,
              }}
            >
              <Typography variant="h6" sx={{ color: '#0d47a1', fontWeight: '600' }}>
                Ausgewählter Termin:
              </Typography>
              <Typography variant="body1" sx={{ color: '#1565c0' }}>
                {dayjs(selectedDate).format('dddd, D. MMMM YYYY')} um {selectedSlot}
              </Typography>
            </Box>
          )}

          {/* Confirmation Dialog */}
          <Dialog open={confirmationOpen} onClose={cancelSlotSelection}>
            <DialogTitle>Termin bestätigen</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {`Sie haben einen Termin am ${dayjs(selectedDate).format(
                  'dddd, D. MMMM YYYY'
                )} um ${selectedSlot} ausgewählt. Möchten Sie diesen Termin bestätigen?`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={cancelSlotSelection} color="secondary" variant="outlined">
                Cancel
              </Button>
              <Button onClick={confirmSlotSelection} color="primary" variant="contained">
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default ScheduleAppointment;
